import usePageTitle from '../../hooks/usePageTitle';
import React, { useState, useEffect, useRef } from 'react';
import { activity } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Users, 
  Eye, 
  Search, 
  ShoppingCart,
  TrendingUp,
  Clock,
  User,
  Package,
  RefreshCw,
  Activity,
  MousePointer
} from 'lucide-react';
import { toast } from 'sonner';

const AdminActivity = () => {
  usePageTitle('Customer Activity');
  const [dashboard, setDashboard] = useState(null);
  const [liveVisitors, setLiveVisitors] = useState([]);
  const [recentEvents, setRecentEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const refreshInterval = useRef(null);

  useEffect(() => {
    loadDashboard();
    loadLiveVisitors();
    loadRecentEvents();

    // Auto-refresh every 30 seconds
    if (autoRefresh) {
      refreshInterval.current = setInterval(() => {
        loadDashboard();
        loadLiveVisitors();
      }, 30000);
    }

    return () => {
      if (refreshInterval.current) {
        clearInterval(refreshInterval.current);
      }
    };
  }, [autoRefresh]);

  const loadDashboard = async () => {
    try {
      const response = await activity.getDashboard();
      setDashboard(response.data);
    } catch (error) {
      console.error('Failed to load dashboard:', error);
    }
  };

  const loadLiveVisitors = async () => {
    try {
      const response = await activity.getLiveVisitors();
      setLiveVisitors(response.data.visitors || []);
    } catch (error) {
      console.error('Failed to load live visitors:', error);
    }
  };

  const loadRecentEvents = async () => {
    try {
      const response = await activity.getRecent(100);
      setRecentEvents(response.data.events || []);
    } catch (error) {
      console.error('Failed to load recent events:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    setLoading(true);
    Promise.all([loadDashboard(), loadLiveVisitors(), loadRecentEvents()])
      .then(() => toast.success('Activity refreshed'))
      .finally(() => setLoading(false));
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diff = Math.floor((now - time) / 1000);

    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    
    // For older events, show Australian time
    const australianTime = new Date(time.toLocaleString('en-US', { timeZone: 'Australia/Sydney' }));
    return australianTime.toLocaleDateString('en-AU', { 
      day: 'numeric', 
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getEventIcon = (eventType) => {
    switch (eventType) {
      case 'product_view': return <Eye className="w-4 h-4 text-blue-500" />;
      case 'search': return <Search className="w-4 h-4 text-purple-500" />;
      case 'category_view': return <Package className="w-4 h-4 text-green-500" />;
      case 'cart_add': return <ShoppingCart className="w-4 h-4 text-orange-500" />;
      case 'page_view': return <MousePointer className="w-4 h-4 text-gray-500" />;
      default: return <Activity className="w-4 h-4 text-gray-400" />;
    }
  };

  const getEventDescription = (event) => {
    switch (event.event_type) {
      case 'product_view':
        return `Viewed "${event.product_name || 'Unknown product'}"`;
      case 'search':
        return `Searched for "${event.search_query}" (${event.search_results_count || 0} results)`;
      case 'category_view':
        return `Browsing ${event.category_name || 'category'}`;
      case 'cart_add':
        return `Added "${event.product_name || 'item'}" to cart`;
      case 'page_view':
        return `Visited ${event.page_title || event.page_url || 'page'}`;
      default:
        return event.event_type;
    }
  };

  if (loading && !dashboard) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Customer Activity</h1>
          <p className="text-gray-500 text-sm">Track what customers are viewing and searching for</p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-gray-600">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="rounded border-gray-300"
            />
            Auto-refresh
          </label>
          <Button variant="outline" size="sm" onClick={handleRefresh} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Active Now</p>
                <p className="text-3xl font-bold text-green-600">{dashboard?.active_visitors || 0}</p>
              </div>
              <div className="p-3 bg-green-100 rounded-full">
                <Users className="w-6 h-6 text-green-600" />
              </div>
            </div>
            <p className="text-xs text-gray-400 mt-2">Last 15 minutes</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Today's Visitors</p>
                <p className="text-3xl font-bold text-blue-600">{dashboard?.today_visitors || 0}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-full">
                <TrendingUp className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Product Views</p>
                <p className="text-3xl font-bold text-purple-600">{dashboard?.product_views_today || 0}</p>
              </div>
              <div className="p-3 bg-purple-100 rounded-full">
                <Eye className="w-6 h-6 text-purple-600" />
              </div>
            </div>
            <p className="text-xs text-gray-400 mt-2">Today</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Searches</p>
                <p className="text-3xl font-bold text-orange-600">{dashboard?.searches_today || 0}</p>
              </div>
              <div className="p-3 bg-orange-100 rounded-full">
                <Search className="w-6 h-6 text-orange-600" />
              </div>
            </div>
            <p className="text-xs text-gray-400 mt-2">Today</p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="live" className="space-y-4">
        <TabsList>
          <TabsTrigger value="live" className="flex items-center gap-2">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
            </span>
            Live Visitors ({liveVisitors.length})
          </TabsTrigger>
          <TabsTrigger value="products">Popular Products</TabsTrigger>
          <TabsTrigger value="searches">Search Trends</TabsTrigger>
          <TabsTrigger value="recent">Recent Activity</TabsTrigger>
        </TabsList>

        {/* Live Visitors */}
        <TabsContent value="live">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <span className="relative flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
                </span>
                Live Visitors
              </CardTitle>
              <CardDescription>Currently active on your store (last 15 minutes)</CardDescription>
            </CardHeader>
            <CardContent>
              {liveVisitors.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No active visitors right now</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {liveVisitors.map((visitor) => (
                    <div key={visitor.session_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-full ${visitor.is_logged_in ? 'bg-blue-100' : 'bg-gray-200'}`}>
                          <User className={`w-4 h-4 ${visitor.is_logged_in ? 'text-blue-600' : 'text-gray-500'}`} />
                        </div>
                        <div>
                          <p className="font-medium text-sm">
                            {visitor.is_logged_in ? (
                              <span>{visitor.user_name || visitor.user_email}</span>
                            ) : (
                              <span className="text-gray-500">Anonymous Visitor</span>
                            )}
                          </p>
                          <p className="text-xs text-gray-500 flex items-center gap-1">
                            {getEventIcon(visitor.last_event_type)}
                            {visitor.last_product_name ? (
                              <span>Viewing: {visitor.last_product_name}</span>
                            ) : visitor.last_search_query ? (
                              <span>Searched: "{visitor.last_search_query}"</span>
                            ) : (
                              <span>{visitor.last_event_type}</span>
                            )}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <Badge variant={visitor.is_logged_in ? 'default' : 'secondary'}>
                          {visitor.is_logged_in ? 'Logged In' : 'Guest'}
                        </Badge>
                        <p className="text-xs text-gray-400 mt-1">{formatTimeAgo(visitor.last_activity)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Popular Products */}
        <TabsContent value="products">
          <Card>
            <CardHeader>
              <CardTitle>Most Viewed Products</CardTitle>
              <CardDescription>Top products by views in the last 7 days</CardDescription>
            </CardHeader>
            <CardContent>
              {!dashboard?.popular_products?.length ? (
                <div className="text-center py-8 text-gray-500">
                  <Package className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No product views yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {dashboard.popular_products.map((product, idx) => (
                    <div key={product.product_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 flex items-center justify-center bg-blue-100 text-blue-600 rounded-full font-bold text-sm">
                          {idx + 1}
                        </div>
                        <div>
                          <p className="font-medium text-sm">{product.product_name}</p>
                          <p className="text-xs text-gray-500">
                            SKU: {product.product_sku} • {product.product_category}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-blue-600">{product.view_count} views</p>
                        <p className="text-xs text-gray-400">{product.unique_viewers} unique visitors</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Search Trends */}
        <TabsContent value="searches">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle>Popular Searches</CardTitle>
                <CardDescription>What customers are searching for (last 7 days)</CardDescription>
              </CardHeader>
              <CardContent>
                {!dashboard?.popular_searches?.length ? (
                  <div className="text-center py-8 text-gray-500">
                    <Search className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>No searches yet</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {dashboard.popular_searches.map((search, idx) => (
                      <div key={search.term} className="flex items-center justify-between p-2 hover:bg-gray-50 rounded">
                        <div className="flex items-center gap-2">
                          <Search className="w-4 h-4 text-gray-400" />
                          <span className="font-medium">"{search.term}"</span>
                        </div>
                        <Badge variant="secondary">{search.count} searches</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Popular Categories</CardTitle>
                <CardDescription>Most browsed categories (last 7 days)</CardDescription>
              </CardHeader>
              <CardContent>
                {!dashboard?.popular_categories?.length ? (
                  <div className="text-center py-8 text-gray-500">
                    <Package className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>No category views yet</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {dashboard.popular_categories.map((cat) => (
                      <div key={cat.name} className="flex items-center justify-between p-2 hover:bg-gray-50 rounded">
                        <span className="font-medium capitalize">{cat.name}</span>
                        <Badge variant="secondary">{cat.views} views</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Recent Activity */}
        <TabsContent value="recent">
          <Card>
            <CardHeader>
              <CardTitle>Recent Activity</CardTitle>
              <CardDescription>Latest 100 events across all visitors</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-[500px] overflow-y-auto">
                {recentEvents.map((event) => (
                  <div key={event.id} className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded text-sm">
                    {getEventIcon(event.event_type)}
                    <div className="flex-1">
                      <span className="font-medium">
                        {event.user_name || event.user_email || 'Anonymous'}
                      </span>
                      <span className="text-gray-500 ml-2">{getEventDescription(event)}</span>
                    </div>
                    <span className="text-xs text-gray-400">{formatTimeAgo(event.timestamp)}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminActivity;
